/**
 * Функции валидации форм и данных
 */

/**
 * Проверка, является ли значение числом
 * 
 * @param {any} value - Проверяемое значение
 * @returns {boolean} true, если значение является числом
 */
export const isNumber = (value) => {
  if (typeof value === 'number') return !isNaN(value);
  if (typeof value !== 'string') return false;
  
  // Удаляем пробелы
  const trimmed = value.trim();
  if (trimmed === '') return false;
  
  // Пробуем преобразовать в число
  return !isNaN(Number(trimmed));
};

/**
 * Проверка, является ли значение целым числом
 * 
 * @param {any} value - Проверяемое значение
 * @returns {boolean} true, если значение является целым числом
 */
export const isInteger = (value) => {
  if (!isNumber(value)) return false;
  
  const number = Number(value);
  return Number.isInteger(number);
};

/**
 * Проверка, является ли значение положительным числом
 * 
 * @param {any} value - Проверяемое значение
 * @returns {boolean} true, если значение является положительным числом
 */
export const isPositiveNumber = (value) => {
  if (!isNumber(value)) return false;
  
  const number = Number(value);
  return number > 0;
};

/**
 * Проверка, является ли значение неотрицательным числом
 * 
 * @param {any} value - Проверяемое значение
 * @returns {boolean} true, если значение является неотрицательным числом
 */
export const isNonNegativeNumber = (value) => {
  if (!isNumber(value)) return false;
  
  const number = Number(value);
  return number >= 0;
};

/**
 * Проверка, находится ли число в заданном диапазоне
 * 
 * @param {any} value - Проверяемое значение
 * @param {number} min - Минимальное значение
 * @param {number} max - Максимальное значение
 * @returns {boolean} true, если значение находится в диапазоне
 */
export const isInRange = (value, min, max) => {
  if (!isNumber(value)) return false;
  
  const number = Number(value);
  return number >= min && number <= max;
};

/**
 * Проверка, является ли строка пустой
 * 
 * @param {string} value - Проверяемая строка
 * @returns {boolean} true, если строка пуста
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  return String(value).trim() === '';
};

/**
 * Проверка минимальной длины строки
 * 
 * @param {string} value - Проверяемая строка
 * @param {number} minLength - Минимальная длина
 * @returns {boolean} true, если длина строки не меньше minLength
 */
export const hasMinLength = (value, minLength) => {
  if (value === null || value === undefined) return false;
  return String(value).length >= minLength;
};

/**
 * Проверка максимальной длины строки
 * 
 * @param {string} value - Проверяемая строка
 * @param {number} maxLength - Максимальная длина
 * @returns {boolean} true, если длина строки не больше maxLength
 */
export const hasMaxLength = (value, maxLength) => {
  if (value === null || value === undefined) return true;
  return String(value).length <= maxLength;
};

/**
 * Проверка электронной почты
 * 
 * @param {string} email - Проверяемый email
 * @returns {boolean} true, если email корректен
 */
export const isValidEmail = (email) => {
  if (isEmpty(email)) return false;
  
  // Простая регулярка для проверки формата email
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Проверка телефонного номера
 * 
 * @param {string} phone - Проверяемый телефон
 * @returns {boolean} true, если телефон корректен
 */
export const isValidPhone = (phone) => {
  if (isEmpty(phone)) return false;
  
  // Удаляем все нецифровые символы
  const digits = phone.replace(/\D/g, '');
  
  // Проверяем, что телефон содержит от 10 до 15 цифр
  return digits.length >= 10 && digits.length <= 15;
};

/**
 * Проверка даты
 * 
 * @param {string|Date} date - Проверяемая дата
 * @returns {boolean} true, если дата корректна
 */
export const isValidDate = (date) => {
  if (!date) return false;
  
  // Преобразуем в объект Date
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Проверяем валидность
  return !isNaN(dateObj.getTime());
};

/**
 * Проверка пароля на сложность
 * 
 * @param {string} password - Проверяемый пароль
 * @param {number} minLength - Минимальная длина
 * @param {boolean} requireLower - Требуется ли наличие строчных букв
 * @param {boolean} requireUpper - Требуется ли наличие заглавных букв
 * @param {boolean} requireDigit - Требуется ли наличие цифр
 * @param {boolean} requireSpecial - Требуется ли наличие спецсимволов
 * @returns {Object} Результат проверки с оценкой сложности
 */
export const validatePassword = (
  password,
  minLength = 8,
  requireLower = true,
  requireUpper = true,
  requireDigit = true,
  requireSpecial = false
) => {
  const result = {
    valid: true,
    hasMinLength: hasMinLength(password, minLength),
    hasLower: /[a-z]/.test(password),
    hasUpper: /[A-Z]/.test(password),
    hasDigit: /\d/.test(password),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    strength: 0, // 0-100
    errors: []
  };
  
  // Проверяем минимальную длину
  if (!result.hasMinLength) {
    result.errors.push(`Пароль должен содержать минимум ${minLength} символов`);
    result.valid = false;
  }
  
  // Проверяем наличие строчных букв
  if (requireLower && !result.hasLower) {
    result.errors.push('Пароль должен содержать строчные буквы');
    result.valid = false;
  }
  
  // Проверяем наличие заглавных букв
  if (requireUpper && !result.hasUpper) {
    result.errors.push('Пароль должен содержать заглавные буквы');
    result.valid = false;
  }
  
  // Проверяем наличие цифр
  if (requireDigit && !result.hasDigit) {
    result.errors.push('Пароль должен содержать цифры');
    result.valid = false;
  }
  
  // Проверяем наличие спецсимволов
  if (requireSpecial && !result.hasSpecial) {
    result.errors.push('Пароль должен содержать специальные символы');
    result.valid = false;
  }
  
  // Расчет сложности пароля
  let strength = 0;
  
  // Базовая сложность по длине
  if (password) {
    strength += Math.min(password.length * 4, 40);
  }
  
  // Дополнительные баллы за разнообразие символов
  if (result.hasLower) strength += 10;
  if (result.hasUpper) strength += 10;
  if (result.hasDigit) strength += 10;
  if (result.hasSpecial) strength += 20;
  
  // Штраф за повторяющиеся символы
  const repeats = password ? (password.length - new Set(password).size) : 0;
  strength -= repeats * 2;
  
  // Ограничиваем диапазон от 0 до 100
  result.strength = Math.max(0, Math.min(100, strength));
  
  return result;
};

/**
 * Проверка строки на соответствие регулярному выражению
 * 
 * @param {string} value - Проверяемая строка
 * @param {RegExp} pattern - Регулярное выражение
 * @returns {boolean} true, если строка соответствует регулярному выражению
 */
export const matchesPattern = (value, pattern) => {
  if (value === null || value === undefined) return false;
  return pattern.test(String(value));
};

/**
 * Проверка валидности данных формы
 * 
 * @param {Object} formData - Данные формы
 * @param {Object} validationRules - Правила валидации
 * @returns {Object} Результат валидации с ошибками
 */
export const validateForm = (formData, validationRules) => {
  const result = {
    valid: true,
    errors: {}
  };
  
  // Проходим по всем правилам
  for (const field in validationRules) {
    if (validationRules.hasOwnProperty(field)) {
      const rules = validationRules[field];
      const value = formData[field];
      const fieldErrors = [];
      
      // Проверяем каждое правило для поля
      for (const rule of rules) {
        const { type, message, ...params } = rule;
        
        let isValid = true;
        
        // Применяем соответствующий метод валидации
        switch (type) {
          case 'required':
            isValid = !isEmpty(value);
            break;
          case 'number':
            isValid = isNumber(value);
            break;
          case 'integer':
            isValid = isInteger(value);
            break;
          case 'positive':
            isValid = isPositiveNumber(value);
            break;
          case 'nonNegative':
            isValid = isNonNegativeNumber(value);
            break;
          case 'range':
            isValid = isInRange(value, params.min, params.max);
            break;
          case 'minLength':
            isValid = hasMinLength(value, params.length);
            break;
          case 'maxLength':
            isValid = hasMaxLength(value, params.length);
            break;
          case 'email':
            isValid = isValidEmail(value);
            break;
          case 'phone':
            isValid = isValidPhone(value);
            break;
          case 'date':
            isValid = isValidDate(value);
            break;
          case 'pattern':
            isValid = matchesPattern(value, params.pattern);
            break;
          case 'custom':
            isValid = params.validate(value, formData);
            break;
          default:
            console.warn(`Unknown validation rule type: ${type}`);
            isValid = true;
        }
        
        // Если проверка не прошла, добавляем ошибку
        if (!isValid) {
          fieldErrors.push(message || `Поле ${field} имеет некорректное значение`);
          result.valid = false;
        }
      }
      
      // Если есть ошибки для поля, добавляем в результат
      if (fieldErrors.length > 0) {
        result.errors[field] = fieldErrors;
      }
    }
  }
  
  return result;
};